import React, { useState, useEffect, useRef, useCallback } from "react"
import { makeStyles } from "tss-react/mui"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import {
  Paper,
  Typography,
  Button,
  // TextField,        // search field hidden for now
  // Checkbox,
  // FormControlLabel,
  Badge,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  // InputAdornment,   // search field hidden for now
} from "@mui/material"
import {
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  // Search as SearchIcon,   // search field hidden for now
  Visibility as EyeIcon,
  VisibilityOff as EyeOffIcon,
  FiberManualRecord as OnlineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Settings as SettingsIcon,
  Star as FriendStarIcon,
} from "@mui/icons-material"
// The row context menu itself now lives in App (one shared instance, summoned
// from these rows AND chat usernames). This module keeps only the gestures;
// IS_MAC / LONG_PRESS_MS / the row enrichment are shared from there.
import { IS_MAC, LONG_PRESS_MS, enrichUser } from "./UserContextMenu"
import { formatRelativeTime } from "../utils/formatTime"
import { canRestrictRole } from "../utils/roleRank"
import { resolveAvatarUrl } from "../services/api"
import AvatarFallback from "./AvatarFallback"
import JesterHat from "./JesterHat"

const useStyles = makeStyles()((theme) => ({
  sidebar: {
    width: 280,
    flexShrink: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    transition: "width 200ms ease",
    // Mockup `.rail`: a translucent surface (in sesh) so the body's radial-glow
    // backdrop shows through, rather than a flat opaque panel. Midnight's railBg
    // is opaque, so it looks unchanged there.
    backgroundColor: theme.palette.custom.railBg,
    borderLeft: `1px solid ${theme.palette.custom.line}`,
  },
  sidebarCollapsed: {
    width: 0,
    borderLeft: "none",
  },
  // Mobile: take the rail out of flow entirely and float it as a right-edge
  // overlay drawer, so the chat column gets the full viewport width. It keeps
  // its 280px width but is position:fixed and slides off-screen when closed.
  sidebarMobile: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    height: "100%",
    zIndex: theme.zIndex.drawer,
    width: 280,
    maxWidth: "85vw",
    boxShadow: "-8px 0 24px rgba(0,0,0,0.45)",
    transform: "translateX(0)",
    transition: "transform 220ms ease",
  },
  // Closed mobile drawer: slid fully off the right edge. We DON'T set width:0
  // (that would collapse the body); instead we translate it away so the open
  // animation slides it back in.
  sidebarMobileClosed: {
    transform: "translateX(100%)",
    boxShadow: "none",
  },
  // Scrim behind the open mobile drawer — tap to dismiss.
  mobileBackdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: theme.zIndex.drawer - 1,
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
  },
  // The reopen handle for the mobile drawer when it's closed: a small tab
  // pinned to the right edge of the screen (the in-flow collapseTab is hidden
  // off-screen with the drawer, so mobile needs its own always-visible handle).
  mobileOpenTab: {
    position: "fixed",
    top: "50%",
    right: 0,
    transform: "translateY(-50%)",
    width: 22,
    height: 52,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.custom.railBg,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRight: "none",
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    color: theme.palette.text.secondary,
    zIndex: theme.zIndex.drawer,
    padding: 0,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.custom.panel,
    },
  },
  collapseTab: {
    position: "absolute",
    top: "50%",
    left: -18,
    transform: "translateY(-50%)",
    width: 18,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.custom.railBg,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRight: "none",
    borderRadius: 0,
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    cursor: "pointer",
    color: theme.palette.text.secondary,
    zIndex: 5,
    padding: 0,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.custom.panel,
    },
  },
  // Flex-column wrapper inside the sidebar that lays out the user list and the
  // webcam panel. flex:1 so it fills the sidebar's height; the user list inside
  // grows to take the slack while the webcam panel stays its natural size.
  sidebarBody: {
    flex: 1,
    minHeight: 0, // allow children to shrink/scroll instead of overflowing
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },
  // User list section: takes the majority of the vertical space. The online
  // counter is pinned at the top; the user list (.usersList) below it grows
  // and scrolls its overflow.
  userListSection: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  tabs: {
    backgroundColor: theme.palette.custom.panel,
    "& .MuiTabs-indicator": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  tab: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    textWrap: "nowrap",
    color: theme.palette.text.secondary,
    minWidth: 0,
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  content: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflowY: "auto",
  },
  webcamButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.custom.accentContrastText,
    fontWeight: 600,
    padding: theme.spacing(1.5),
    width: "100%",
    "&:hover": {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  // Inline self-monitor (shown in place of the Enable button while publishing):
  // a live view of our own SFU output so we can monitor what others receive.
  // Styled after the mockup's `.self-cam`: the cam/mic controls live IN the
  // preview on a bottom gradient bar alongside the "you · name" label, rather
  // than as a separate pill row above it.
  selfMonitorPanel: {
    width: "100%",
    backgroundColor: theme.palette.custom.videoTileBg,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    position: "relative",
    // border: `1px solid ${theme.palette.custom.lineStrong}`,
    border: 0,
  },
  selfMonitorBody: {
    position: "relative",
    aspectRatio: "16 / 10",
    backgroundColor: "#000000",
  },
  selfMonitorVideo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  selfMonitorPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  // Gradient bar overlaid on the bottom of the preview: name on the left,
  // cam/mic toggle buttons on the right (mockup `.self-cam .bar`).
  selfMonitorBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1, 1.25),
    display: "flex",
    alignItems: "center",
    justifyContent: "right",
    gap: theme.spacing(1),
    // background: "linear-gradient(0deg, transparent, rgba(6,11,8,0.9))",
    pointerEvents: "none", // let the video underneath stay clickable...
  },
  selfMonitorName: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#fff",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textShadow: "0 1px 4px rgba(0,0,0,.6)",
  },
  // The cam/mic button cluster re-enables pointer events (parent bar disables).
  selfMonitorControls: {
    display: "flex",
    gap: theme.spacing(0.75),
    pointerEvents: "auto",
  },
  // Square translucent control button, mockup `.cam-toggle button`.
  selfMonitorCtrlBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.16)",
    backgroundColor: "rgba(10,15,12,0.55)",
    color: "#fff",
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    padding: 0,
    transition: "all 0.15s",
    "&:hover": {
      backgroundColor: "rgba(10,15,12,0.85)",
      borderColor: "rgba(255,255,255,0.32)",
    },
    "& svg": { fontSize: 17 },
    // "muted"/off state: filled ember (mockup `.cam-toggle button.muted`).
    "&.off": {
      backgroundColor: theme.palette.custom.emberDeep,
      borderColor: theme.palette.custom.ember,
      color: "#fff",
    },
  },
  disableWebcamButton: {
    width: "100%",
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "rgba(255, 1, 66, 0.08)",
      borderColor: theme.palette.error.main,
    },
  },
  // Draggable/collapsible webcam panel wrapper
  webcamPanel: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    margin: theme.spacing(0),
    "&.dragging": {
      opacity: 0.7,
      outline: `2px dashed ${theme.palette.primary.main}`,
    },
  },
  webcamPanelHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.custom.panel,
    borderBottom: `1px solid ${theme.palette.custom.line}`,
    userSelect: "none",
    cursor: "default",
  },
  webcamPanelDragHandle: {
    cursor: "grab",
    color: "rgba(255,255,255,0.3)",
    display: "flex",
    alignItems: "center",
    padding: "2px",
    borderRadius: 4,
    // Pointer-drag relies on capturing pointermove; suppress touch scrolling so
    // a drag on touch devices reorders the panel instead of scrolling the list.
    touchAction: "none",
    "&:hover": {
      color: "rgba(255,255,255,0.7)",
      backgroundColor: "rgba(255,255,255,0.06)",
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  webcamPanelTitle: {
    flex: 1,
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    color: "rgba(255,255,255,0.7)",
  },
  webcamPanelReorderBtn: {
    padding: 2,
    color: "rgba(255,255,255,0.3)",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "rgba(255,255,255,0.06)",
    },
  },
  webcamPanelCollapseBtn: {
    padding: 2,
    color: "rgba(255,255,255,0.4)",
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: "rgba(255,255,255,0.06)",
    },
  },
  // Drop zone indicator shown between sections while dragging
  dropZone: {
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.primary.main,
    margin: theme.spacing(0.25, 0),
    transition: "opacity 0.15s",
  },
  filtersSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  filterCheckbox: {
    "& .MuiFormControlLabel-label": {
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
    },
  },
  onlineCounter: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.custom.panel,
    borderRadius: theme.shape.borderRadius,
  },
  counterText: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.custom.onColor,
  },
  badge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
  },
  // Uppercase letter-spaced section header, mockup `.people-head .lbl`.
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 1, 0.25),
  },
  sectionHeaderLabel: {
    fontSize: "0.6875rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: theme.palette.custom.inkFaint,
    "& b": {
      color: theme.palette.custom.leaf,
      fontFamily: theme.palette.custom.fontMono,
    },
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#1a1f2e",
      fontSize: "0.875rem",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.3)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  usersList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    // Mockup `.people` inset: a little side padding + bottom breathing room so
    // rows don't butt against the panel edge. Tight row gap; the row's own
    // padding does the vertical spacing.
    padding: "4px 6px 16px",
    gap: 2,
  },
  userItem: {
    display: "flex",
    alignItems: "center",
    gap: 11, // mockup `.person` gap
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    // Cursor honesty: on Mac a plain left-click opens the row menu, so show the
    // pointer there; on other platforms the menu is right-click/long-press only,
    // so a plain click does nothing → default cursor.
    cursor: IS_MAC ? "pointer" : "default",
    position: "relative",
    "&:hover": {
      backgroundColor: theme.palette.custom.hoverOverlay,
    },
  },
  // Rounded-square avatar (mockup `.person .av`) — radius comes from the theme
  // (50% for midnight = circle, 11px for sesh = rounded square).
  userAvatar: {
    width: 36,
    height: 36,
    fontSize: "0.8125rem",
    fontWeight: 700,
    borderRadius: theme.palette.custom.avatarRadius,
    background: theme.palette.custom.avatarBg,
    color: theme.palette.custom.accentContrastText,
    border: `1px solid ${theme.palette.custom.line}`,
    flexShrink: 0,
  },
  // Avatars always paint their own pixels (photo, builtin, or the builtin
  // default fallback) — drop the themed fill behind them. '&&' doubles the
  // class specificity so this deterministically beats userAvatar above AND
  // MUI's .MuiAvatar-colorDefault; an sx override here lost to userAvatar
  // depending on emotion injection order.
  userAvatarPhoto: {
    "&&": { background: "transparent" },
  },
  userInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0, // Allow flex shrink
  },
  usernameLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  // Sidebar usernames read as clean white ink for everyone (mockup `.pn`),
  // with WEIGHT as the only role cue (staff bolder). Not the per-user message
  // color, which colors names in the chat messages instead.
  userName: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: theme.palette.custom.ink, // white-ish ink for all roles
    "&.staff": {
      fontWeight: 700, // admin/owner: bold
    },
  },
  userCountryFlag: {
    fontSize: "0.75rem",
  },
  // Small star after the name of an accepted friend (Phase 3). Sized to sit
  // inline with the country flag / username without shoving the row.
  friendStar: {
    fontSize: "0.8125rem",
    color: theme.palette.secondary.main, // green accent — matches the friend theme
    flexShrink: 0,
  },
  // Subtle attention dot on a row with a PENDING incoming friend request, so
  // "click a name to respond" has something to find. Distinct from the presence
  // dot (this rides in the name line, that on the row's right edge).
  friendRequestDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.palette.primary.main, // teal — an unread/attention cue
    boxShadow: `0 0 4px ${theme.palette.primary.main}`,
  },
  // Presence dot on the right edge of the row (mockup `.presence-dot`).
  // Color now reflects activity-based presence, not webcam state:
  //   active → green (with glow), idle → yellow, away/busy → red.
  presenceDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
    transition: "background-color 0.3s ease, box-shadow 0.3s ease",
    "&.active": {
      backgroundColor: theme.palette.custom.presenceActive,
      boxShadow: `0 0 8px ${theme.palette.custom.presenceActive}`,
    },
    "&.idle": {
      backgroundColor: theme.palette.custom.presenceIdle,
    },
    "&.away": {
      backgroundColor: theme.palette.custom.presenceAway,
    },
  },
  userAge: {
    fontSize: "0.625rem",
    color: theme.palette.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: "2px 4px",
    borderRadius: 2,
    minWidth: 20,
    textAlign: "center",
  },
  // Meta line under the username (relative time, webcam icon) — mono font like
  // the mockup's `.person .pmeta`.
  userStats: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.6875rem",
    fontFamily: theme.palette.custom.fontMono,
    color: theme.palette.custom.inkFaint,
  },
  userActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.625rem",
    color: theme.palette.text.secondary,
  },
  actionButton: {
    padding: 4,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      color: theme.palette.text.primary,
    },
  },
  unreadBadge: {
    backgroundColor: theme.palette.error.main,
    color: "#ffffff",
    minWidth: 16,
    height: 16,
    fontSize: "0.625rem",
  },

  // Status pill indicators — `onIndicator` tints the online-counter dot; both
  // are theme-aware (green "on" / muted "off").
  onIndicator: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    color: theme.palette.custom.onColor,
  },
  offIndicator: {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    color: theme.palette.custom.offColor,
  },
}))

// formatRelativeTime (login/join "Nm/Nh/Nd" relative time) now lives in
// ../utils/formatTime and is shared with the chat message list (imported above).

// Binds a MediaStream to a <video> srcObject (can't be set via JSX prop).
// Used by the inline self-monitor.
const SelfMonitorVideo = ({ stream, className }) => {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (el && stream && el.srcObject !== stream) el.srcObject = stream
  }, [stream])
  return <video ref={ref} className={className} autoPlay playsInline muted />
}

const RightSidebar = ({
  onlineUsers = [],
  watchingUsers = [],
  watchedByUsers = [],
  blockedWatchers = [],
  blockedByUsers = [],
  deniedCooldownUsers = [],
  onWatchUser,
  onUnwatchUser,
  onToggleWatchPermission,
  onOpenUserMenu,
  // Friends v1: Sets of user ids. friendIds get a star glyph + sort-boost within
  // their role rank; incomingRequestIds get a subtle attention dot so "click a
  // name to respond" has a visible target. Outgoing shows nothing.
  // friendsEnabled gates ALL friend UI (Phase 6 tenant flag, default ON).
  friendIds = new Set(),
  incomingRequestIds = new Set(),
  friendsEnabled = true,
  producersByUser = {},
  pausedVideoUsers = [],
  currentUserId,
  // My instance role — gates the per-watcher cam-block eye (can't block someone
  // who outranks me; the server enforces the same rule).
  currentUserRole,
  isGuest = false,
  isPublishing = false,
  selfStream = null,
  videoTrackOn = true,
  micEnabled = true,
  onToggleVideoTrack,
  onToggleMicTrack,
  onEnableWebcam,
  onDisableWebcam,
  onWebcamModeChange,
  onOpenDeviceSetup,
  // userId currently wearing the "noodle shame" jester hat (or null). See
  // JesterHat / App.js noodleShame.
  noodledUserId = null,
}) => {
  const { classes, cx } = useStyles()
  // Below this width the rail can't share a row with chat without crushing it,
  // so we float it as an overlay drawer instead (see render). Matches the
  // app-wide mobile breakpoint.
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md")) // < 900px
  const [activeTab, setActiveTab] = useState(0)
  // Desktop collapse: persisted, shrinks the in-flow rail to a thin tab.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("cc.rightSidebar.collapsed") === "1" } catch { return false }
  })
  // Mobile drawer open state: NOT persisted and starts closed, so a phone user
  // lands on a full-width chat and opens the user list on demand. Kept separate
  // from `collapsed` so toggling on mobile doesn't clobber the desktop pref.
  const [mobileOpen, setMobileOpen] = useState(false)
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try { localStorage.setItem("cc.rightSidebar.collapsed", next ? "1" : "0") } catch {}
      return next
    })
  }, [])
  // On mobile the same tab toggles the overlay drawer; on desktop it toggles the
  // persisted in-flow collapse.
  const toggleSidebar = useCallback(() => {
    if (isMobile) setMobileOpen((p) => !p)
    else toggleCollapsed()
  }, [isMobile, toggleCollapsed])
  // Webcam on/off now reflects the real publishing state lifted from App.
  const webcamEnabled = isPublishing
  const [searchQuery, setSearchQuery] = useState("")
  // 'above' | 'below' — whether webcam panel sits above or below the user list
  const [webcamPanelPosition, setWebcamPanelPosition] = useState("below")
  // Collapsed state for the whole webcam panel (hides tabs + content)
  const [webcamPanelCollapsed, setWebcamPanelCollapsed] = useState(false)
  // Drag-to-reorder state. We use a pointer-drag (not HTML5 DnD) because there
  // are only two slots — the panel goes above or below the user list — and
  // pointer events are far more reliable than native drag for this. While
  // dragging we show a drop indicator at whichever edge the pointer is nearest;
  // releasing commits that position.
  const [isDragging, setIsDragging] = useState(false)
  const [dropTarget, setDropTarget] = useState(null) // 'above' | 'below' | null
  const sidebarRef = useRef(null)
  const userListRef = useRef(null)

  // Tick once a minute so relative join times — and the activity-based presence
  // dots — advance ("Now" → "1m" → …, active → idle → away) even when the user
  // list itself isn't changing.
  const [, setNowTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60 * 1000)
    return () => clearInterval(id)
  }, [])
  // Single clock read per render, shared by every row's presence calc below.
  const presenceNow = Date.now()

  // Pointer-drag on the panel header's grip. We track the pointer over the
  // sidebar's two children (webcam panel + user list) and decide the drop slot
  // from which half of the sidebar the pointer is in: top half → "above",
  // bottom half → "below".
  const handleGripPointerDown = useCallback(
    (e) => {
      // Left button only; ignore if the header's other controls were hit.
      if (e.button !== 0) return
      e.preventDefault()

      // Threshold on the USER LIST's own midpoint, not the sidebar's. The user
      // list is the thing the panel reorders around, so "pointer above the
      // list's center → drop above, below its center → drop below" is symmetric
      // in both directions. (Using the sidebar midpoint broke above→below: when
      // the panel was already on top, its grip started near the sidebar top and
      // the long list pushed the midpoint far down, so a normal drag never
      // crossed it.) Fall back to the sidebar midpoint if the list isn't mounted.
      const computeTarget = (clientY) => {
        const listEl = userListRef.current
        const el = listEl || sidebarRef.current
        if (!el) return webcamPanelPosition
        const rect = el.getBoundingClientRect()
        const mid = rect.top + rect.height / 2
        return clientY < mid ? "above" : "below"
      }

      setIsDragging(true)
      setDropTarget(computeTarget(e.clientY))

      const onMove = (ev) => {
        setDropTarget(computeTarget(ev.clientY))
      }
      const onUp = (ev) => {
        const target = computeTarget(ev.clientY)
        setWebcamPanelPosition(target)
        setIsDragging(false)
        setDropTarget(null)
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },
    [webcamPanelPosition],
  )
  // const [filters, setFilters] = useState({
  //   male: true,
  //   female: true,
  //   couple: true,
  // });

  // Mock online users based on live implementation
  const mockUsers = [
    {
      id: "227288",
      username: "entropic_22",
      avatar: "https://html5-chat.com/upload/thumb/227288.jpg",
      lastSeen: "online",
      status: "online",
      role: "user",
      gender: "male",
      hasWebcam: true,
      isWatching: false,
      watchers: 0,
      age: 104,
      country: "US",
      countryFlag: "🇺🇸",
      points: 0,
      credits: 2147483647,
      isMe: true,
      unreadCount: 0,
    },
    {
      id: "227302",
      username: "hashbean_24",
      avatar: "/upload/thumb/227302.jpg",
      lastSeen: "online",
      status: "online",
      role: "user",
      gender: "male",
      hasWebcam: true,
      isWatching: true,
      watchers: 0,
      age: 29,
      country: "DK",
      countryFlag: "🇩🇰",
      points: 0,
      credits: 2147483647,
      isMe: false,
      unreadCount: 2,
    },
    {
      id: "227245",
      username: "CyberSage_77",
      avatar: "/upload/thumb/227245.jpg",
      lastSeen: "online",
      status: "online",
      role: "moderator",
      gender: "female",
      hasWebcam: false,
      isWatching: false,
      watchers: 3,
      age: 25,
      country: "CA",
      countryFlag: "🇨🇦",
      points: 150,
      credits: 500,
      isMe: false,
      unreadCount: 0,
    },
  ]

  // Only show users who are actually connected to the chat room
  const connectedUsers = onlineUsers.filter((user) => user.isOnline === true)
  const displayUsers = connectedUsers.length > 0 ? connectedUsers : []
  const onlineCount = connectedUsers.length

  // Show mock data only if no real connection has been established (for demo purposes)
  const showMockData = false
  const finalDisplayUsers = showMockData ? mockUsers : displayUsers

  const handleTabChange = (event, newValue) => {
    // Guests can't go private — private webcam is a member-only feature.
    if (newValue === 1 && isGuest) return
    console.log("Tab changed to:", newValue)
    setActiveTab(newValue)
    onWebcamModeChange &&
      onWebcamModeChange(newValue === 0 ? "public" : "private")
  }

  const handleWatchToggle = (userId) => {
    const isWatching = watchingUsers.includes(userId)
    if (isWatching) {
      onUnwatchUser && onUnwatchUser(userId)
    } else {
      onWatchUser && onWatchUser(userId)
    }
  }

  // User-row context menu. Clicking a row summons App's single shared
  // UserContextMenu (the same one chat usernames summon) anchored to a screen
  // point, like a real desktop context menu — so a second right-click while
  // it's open just relocates it instead of leaking the browser menu. Triggers:
  // right-click (all platforms), plain left-click (Mac only), and long-press
  // (touch). The caller passes the pointer coords so all three paths anchor
  // identically.
  const openRowMenuAt = (clientX, clientY, rowUser) => {
    onOpenUserMenu && onOpenUserMenu(rowUser.id, { top: clientY, left: clientX })
  }
  // Right-click handler — fires on every platform. Suppresses the native menu
  // and takes the gesture over.
  const handleRowContextMenu = (event, rowUser) => {
    event.preventDefault()
    event.stopPropagation()
    openRowMenuAt(event.clientX, event.clientY, rowUser)
  }
  // Left-click handler — only opens the menu on Mac (elsewhere a plain click on
  // the row does nothing). Clicks bubbling up from the inner action icons are
  // already stopped by that cluster's onClick, so this won't fire for them.
  const handleRowClick = (event, rowUser) => {
    if (!IS_MAC) return
    event.stopPropagation()
    openRowMenuAt(event.clientX, event.clientY, rowUser)
  }
  // Long-press handler for touch devices (no right-click). We arm a timer on
  // touch pointer-down; if the finger stays put past LONG_PRESS_MS we open the
  // menu at the touch point. Any move beyond a small slop, an early lift, or
  // the pointer leaving the row cancels it (so taps and scrolls are unaffected).
  const longPressRef = useRef(null) // { timer, startX, startY } | null
  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current.timer)
      longPressRef.current = null
    }
  }
  const handleRowPointerDown = (event, rowUser) => {
    if (event.pointerType !== "touch") return // mouse/pen use click/right-click
    const { clientX, clientY } = event
    const timer = setTimeout(() => {
      longPressRef.current = null
      openRowMenuAt(clientX, clientY, rowUser)
    }, LONG_PRESS_MS)
    longPressRef.current = { timer, startX: clientX, startY: clientY }
  }
  const handleRowPointerMove = (event) => {
    const lp = longPressRef.current
    if (!lp) return
    // Cancel if the finger drifts past a small slop — that's a scroll, not a press.
    if (
      Math.abs(event.clientX - lp.startX) > 10 ||
      Math.abs(event.clientY - lp.startY) > 10
    ) {
      cancelLongPress()
    }
  }
  // Clear any pending long-press timer on unmount (avoids opening the menu /
  // setState after the component is gone).
  useEffect(() => cancelLongPress, [])

  // const handleFilterChange = (filter) => {
  //   setFilters(prev => ({
  //     ...prev,
  //     [filter]: !prev[filter]
  //   }));
  // };

  const filteredUsers = finalDisplayUsers
    .filter((user) =>
      user.username.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    // Shared enrichment (isMe / hasWebcam / micOnly / presence) — the SAME
    // helper App uses to build the context menu's user, so the rows and the
    // menu can't drift apart. Recomputed every render (the minute ticker
    // re-renders this component) so presence dots age toward idle/away
    // between server broadcasts.
    .map((user) =>
      enrichUser(user, {
        currentUserId,
        isPublishing,
        producersByUser,
        pausedVideoUsers,
        videoTrackOn,
        now: presenceNow,
      })
    )
    // Discord-style hierarchy: staff (owner/admin) first, then regular users,
    // then guests. WITHIN a role rank, accepted friends float above non-friends
    // (Phase 3 sort-boost); alphabetical by username breaks the final tie.
    .sort((a, b) => {
      const rank = (u) => {
        const role = (u.role || "").toLowerCase()
        if (role === "owner" || role === "admin") return 0
        if (role === "guest" || u.isGuest) return 2
        return 1 // member / user
      }
      const byRank = rank(a) - rank(b)
      if (byRank !== 0) return byRank
      // Friend sort-boost: friends float above non-friends within the same role
      // rank. Disabled when the tenant has friendsEnabled=false (Phase 6 gate).
      if (friendsEnabled) {
        const aFriend = friendIds.has(a.id) ? 0 : 1
        const bFriend = friendIds.has(b.id) ? 0 : 1
        if (aFriend !== bFriend) return aFriend - bFriend
      }
      return (a.username || "").localeCompare(b.username || "")
    })

  // Webcam panel as a self-contained element so it can be placed above or below the user list
  const WebcamPanel = (
    <div
      data-id="webcam-panel"
      className={cx(classes.webcamPanel, isDragging && "dragging")}
    >
      {/* Panel header — drag handle + title + reorder arrows + collapse toggle */}
      <div className={classes.webcamPanelHeader}>
        <div
          className={classes.webcamPanelDragHandle}
          onPointerDown={handleGripPointerDown}
          title="Drag to move panel above or below the user list"
        >
          <DragIndicatorIcon style={{ fontSize: 16 }} />
        </div>
        <Typography className={classes.webcamPanelTitle}>Webcam</Typography>
        <Tooltip title="Camera & mic setup">
          <IconButton
            className={classes.webcamPanelReorderBtn}
            size="small"
            onClick={() => onOpenDeviceSetup && onOpenDeviceSetup()}
          >
            <SettingsIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={webcamPanelPosition === "below" ? "Move above user list" : "Move below user list"}>
          <IconButton
            className={classes.webcamPanelReorderBtn}
            size="small"
            onClick={() => setWebcamPanelPosition((p) => p === "below" ? "above" : "below")}
          >
            {webcamPanelPosition === "below"
              ? <ArrowUpIcon style={{ fontSize: 14 }} />
              : <ArrowDownIcon style={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title={webcamPanelCollapsed ? "Expand webcam panel" : "Collapse webcam panel"}>
          <IconButton
            className={classes.webcamPanelCollapseBtn}
            size="small"
            onClick={() => setWebcamPanelCollapsed((v) => !v)}
          >
            {webcamPanelCollapsed
              ? <ExpandMoreIcon style={{ fontSize: 16 }} />
              : <ExpandLessIcon style={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Panel body — hidden when collapsed */}
      {!webcamPanelCollapsed && (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={classes.tabs}
            variant="fullWidth"
          >
            <Tab label="Public Webcam" className={classes.tab} />
            <Tab
              label="Private Webcam"
              className={classes.tab}
              disabled={isGuest}
              title={isGuest ? "Private webcam is not available for guests" : undefined}
            />
          </Tabs>

          <div className={classes.content}>
            {/* Webcam Control. Before publishing: an Enable button. While
              publishing: an inline self-monitor (our own SFU output, so we see
              what others receive) with the cam/mic toggles overlaid IN the
              preview on a bottom gradient bar (mockup-style), plus a Disable
              button below it.

              The cam/mic toggles here don't START publishing — they control
              WHAT goes out in the live publish: the cam button pauses/resumes
              the video track, the mic button the audio track. */}
            {webcamEnabled ? (
              <>
                <div className={classes.selfMonitorPanel}>
                  <div className={classes.selfMonitorBody}>
                    {!videoTrackOn ? (
                      // Video paused: the local track is still live, but watchers
                      // see nothing — show a placeholder so the monitor matches
                      // reality.
                      <div className={classes.selfMonitorPlaceholder}>
                        Camera off
                      </div>
                    ) : selfStream ? (
                      <SelfMonitorVideo
                        stream={selfStream}
                        className={classes.selfMonitorVideo}
                      />
                    ) : (
                      <div className={classes.selfMonitorPlaceholder}>
                        Starting monitor…
                      </div>
                    )}

                    {/* In-preview control bar: name + cam/mic toggles */}
                    <div className={classes.selfMonitorBar}>
                      {/* <span className={classes.selfMonitorName}>Your output</span> */}
                      <div className={classes.selfMonitorControls}>
                        <button
                          type="button"
                          className={cx(classes.selfMonitorCtrlBtn, !videoTrackOn && "off")}
                          onClick={() => onToggleVideoTrack && onToggleVideoTrack()}
                          title={videoTrackOn ? "Stop sending your video" : "Send your video"}
                          aria-label={videoTrackOn ? "Turn camera off" : "Turn camera on"}
                        >
                          {videoTrackOn ? (
                            <VideocamIcon fontSize="small" />
                          ) : (
                            <VideocamOffIcon fontSize="small" />
                          )}
                        </button>
                        <button
                          type="button"
                          className={cx(classes.selfMonitorCtrlBtn, !micEnabled && "off")}
                          onClick={() => onToggleMicTrack && onToggleMicTrack()}
                          title={micEnabled ? "Mute your mic" : "Unmute your mic"}
                          aria-label={micEnabled ? "Mute microphone" : "Unmute microphone"}
                        >
                          {micEnabled ? (
                            <MicIcon fontSize="small" />
                          ) : (
                            <MicOffIcon fontSize="small" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  className={classes.disableWebcamButton}
                  variant="outlined"
                  onClick={() => onDisableWebcam && onDisableWebcam()}
                  startIcon={<VideocamOffIcon />}
                >
                  Disable Webcam
                </Button>
              </>
            ) : (
              <Button
                className={classes.webcamButton}
                variant="contained"
                onClick={() => onEnableWebcam && onEnableWebcam()}
                startIcon={<VideocamIcon />}
              >
                Enable Webcam
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )

  // User list section
  const UserListSection = (
    <div ref={userListRef} className={classes.userListSection}>
      {/* Online Counter */}
      {/* <div className={classes.onlineCounter}>
        <OnlineIcon className={classes.onIndicator} fontSize="small" />
        <Typography className={classes.counterText}>
          Online: {onlineCount}
        </Typography>
        <div className={classes.badge}>Rooms 1</div>
      </div> */}

      {/* Search — hidden for now (not implemented yet). The searchQuery state
          stays (empty => no-op filter) so re-enabling is a one-block change.
      <TextField
        className={classes.searchField}
        placeholder="Search users"
        size="small"
        variant="outlined"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="disabled" />
              </InputAdornment>
            ),
          },
        }}
      /> */}

      {/* Section header — uppercase label + live count (mockup people-head) */}
      <div className={classes.sectionHeader}>
        <span className={classes.sectionHeaderLabel}>
          in the room · <b>{onlineCount}</b>
        </span>
      </div>

      {/* Users List */}
      <div className={classes.usersList}>
        {filteredUsers.map((user) => (
          <div
            key={user.id}
            className={classes.userItem}
            data-id={user.id}
            onContextMenu={(e) => handleRowContextMenu(e, user)}
            onClick={(e) => handleRowClick(e, user)}
            onPointerDown={(e) => handleRowPointerDown(e, user)}
            onPointerMove={handleRowPointerMove}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
            onPointerCancel={cancelLongPress}
          >
            <span
              style={{
                position: "relative",
                display: "inline-flex",
                flexShrink: 0,
                // Let the jester hat perch above and spill past the avatar.
                overflow: "visible",
              }}
            >
              <Avatar
                src={resolveAvatarUrl(user.avatar)}
                className={cx(
                  classes.userAvatar,
                  classes.userAvatarPhoto,
                  user.status
                )}
                alt={user.username}
              >
                <AvatarFallback letter={user.username.charAt(0).toUpperCase()} />
              </Avatar>
              {noodledUserId && user.id === noodledUserId && (
                <JesterHat size={36} title={`${user.username} noodled it 🍜`} />
              )}
            </span>
            <div className={classes.userInfo}>
              <div className={classes.usernameLine}>
                {/* Sidebar names are styled by ROLE only (white / grey guest /
                    bold staff) — intentionally NOT the per-user usernameColor,
                    which colors names in the chat messages instead. */}
                <Typography
                  className={cx(
                    classes.userName,
                    (user.role === "admin" || user.role === "owner") && "staff"
                  )}
                >
                  {user.username}
                </Typography>
                <span className={classes.userCountryFlag}>
                  {user.countryFlag}
                </span>
                {/* Friend star and request dot are gated by friendsEnabled
                    (Phase 6 tenant flag) so they vanish when friends are off. */}
                {friendsEnabled && friendIds.has(user.id) && (
                  <FriendStarIcon
                    className={classes.friendStar}
                    titleAccess={`${user.username} is your friend`}
                  />
                )}
                {friendsEnabled && !friendIds.has(user.id) && incomingRequestIds.has(user.id) && (
                  <span
                    className={classes.friendRequestDot}
                    title={`${user.username} sent you a friend request`}
                  />
                )}
              </div>
              <div className={classes.userStats}>
                <span>
                  {(() => {
                    // Mockup `.pmeta` phrasing: "3h ago" / "17m ago"; very-recent
                    // joins read "Now" (no "ago"). Falls back to raw lastSeen.
                    const rel = user.joinedAt
                      ? formatRelativeTime(user.joinedAt)
                      : null
                    if (rel) return rel === "Now" ? rel : `${rel} ago`
                    return user.lastSeen
                  })()}
                </span>
              </div>
            </div>
            {/* The inline action icons stay direct shortcuts — stop the click
                from bubbling to the row (which would open the context menu). */}
            <div
              className={classes.userActions}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Watch toggle: the camera button. Shown for any publishing
                  user INCLUDING yourself (you can watch your own preview);
                  lit while you're watching them. HIDDEN when that publisher
                  has blocked me from their cam — the ability to watch them
                  goes away entirely. */}
              {user.hasWebcam &&
                !(blockedByUsers.includes(user.id) && !user.isMe) &&
                (() => {
                  const isWatching = watchingUsers.includes(user.id)
                  // Anti-spam cooldown after this user denied my request: dim +
                  // disable the watch button until it expires. Never blocks
                  // STOPPING a watch you already have.
                  const onCooldown =
                    !isWatching && deniedCooldownUsers.includes(user.id)
                  return (
                    <IconButton
                      className={classes.actionButton}
                      size="small"
                      disabled={onCooldown}
                      title={
                        onCooldown
                          ? "Request recently denied — please wait before asking again"
                          : user.micOnly
                            ? isWatching
                              ? user.isMe
                                ? "Microphone only — stop listening to yourself"
                                : "Microphone only — stop listening"
                              : user.isMe
                                ? "Microphone only — listen to yourself"
                                : "Microphone only — listen"
                            : isWatching
                              ? user.isMe
                                ? "Stop watching yourself"
                                : "Stop watching"
                              : user.isMe
                                ? "Watch yourself"
                                : "Watch webcam"
                      }
                      onClick={() => handleWatchToggle(user.id)}
                      style={{
                        color: isWatching ? "#50ce84" : undefined,
                        opacity: onCooldown ? 0.35 : undefined,
                      }}
                    >
                      {user.micOnly ? (
                        <MicIcon fontSize="small" />
                      ) : (
                        <VideocamIcon fontSize="small" />
                      )}
                    </IconButton>
                  )
                })()}
              {/* The "eye" — on each row while I'm publishing — is a per-watcher
                  permission toggle for MY cam:
                    - red          : this user is actively watching me (allowed)
                    - dim           : allowed but not currently watching
                    - slashed (off) : I've blocked this user from my cam
                  Clicking toggles block/unblock; blocking also kicks any active
                  session. My OWN row can't be blocked (no self-block), so there
                  the eye stays display-only and just reflects self-watching. */}
              {isPublishing &&
                (() => {
                  const isBlocked =
                    !user.isMe && blockedWatchers.includes(user.id)
                  const isWatchingMe = watchedByUsers.includes(user.id)
                  // Privilege floor: I may not block a watcher who outranks me —
                  // staff must be able to observe what's published. Hide the
                  // toggle entirely on those rows (the server refuses it too)
                  // rather than showing a control that always fails. An existing
                  // block still renders, so a stale one can be lifted.
                  const outranksMe =
                    !user.isMe && !canRestrictRole(currentUserRole, user.role)
                  if (outranksMe && !isBlocked) return null
                  const title = user.isMe
                    ? isWatchingMe
                      ? "You are watching your own cam"
                      : "You are not watching your own cam"
                    : isBlocked
                      ? `${user.username} is blocked from your cam — click to allow`
                      : isWatchingMe
                        ? `${user.username} is watching your cam — click to block`
                        : `${user.username} can watch your cam — click to block`
                  return (
                    <IconButton
                      className={classes.actionButton}
                      size="small"
                      title={title}
                      onClick={
                        user.isMe
                          ? undefined
                          : () =>
                              onToggleWatchPermission &&
                              onToggleWatchPermission(user.id)
                      }
                    >
                      {isBlocked ? (
                        <EyeOffIcon
                          fontSize="inherit"
                          style={{ color: "#ff9900" }}
                        />
                      ) : (
                        <EyeIcon
                          fontSize="inherit"
                          style={{
                            color: isWatchingMe
                              ? "red"
                              : "rgba(255,255,255,0.25)",
                          }}
                        />
                      )}
                    </IconButton>
                  )
                })()}

              {user.unreadCount > 0 && (
                <Badge
                  badgeContent={user.unreadCount}
                  classes={{ badge: classes.unreadBadge }}
                />
              )}

              {/* Presence dot: activity-based state. green = active (< 5m),
                  yellow = idle (5–30m), red = away/busy (>= 30m). */}
              <span
                className={cx(classes.presenceDot, user.presence)}
                title={
                  user.presence === "active"
                    ? "Active"
                    : user.presence === "idle"
                    ? "Idle"
                    : "Away"
                }
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  // Drop indicator shown while dragging — a bar at the edge the panel would
  // land on. "above" → bar at the very top; "below" → bar at the very bottom.
  const dropIndicator = isDragging ? <div className={classes.dropZone} /> : null

  // On mobile the rail floats as an overlay drawer (off-screen when closed); on
  // desktop it's the in-flow collapsible rail. The body content is identical —
  // only the wrapper class + visibility logic differ.
  const showBody = isMobile ? true : !collapsed

  return (
    <>
      {/* Mobile: tap-to-dismiss scrim behind the open drawer. */}
      {isMobile && mobileOpen && (
        <button
          type="button"
          className={classes.mobileBackdrop}
          aria-label="Close sidebar"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {/* Mobile: always-visible reopen handle when the drawer is closed (the
          in-flow collapse tab slides off-screen with the drawer). */}
      {isMobile && !mobileOpen && (
        <Tooltip title="Show people" placement="left">
          <IconButton
            className={classes.mobileOpenTab}
            onClick={() => setMobileOpen(true)}
            size="small"
            aria-label="Show sidebar"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Paper
        className={cx(
          classes.sidebar,
          !isMobile && collapsed && classes.sidebarCollapsed,
          isMobile && classes.sidebarMobile,
          isMobile && !mobileOpen && classes.sidebarMobileClosed
        )}
        elevation={0}
        square
        ref={sidebarRef}
      >
        {/* Desktop in-flow collapse tab — hidden on mobile (the drawer uses the
            fixed mobileOpenTab + backdrop tap instead). */}
        {!isMobile && (
          <Tooltip title={collapsed ? "Show sidebar" : "Hide sidebar"} placement="left">
            <IconButton
              className={classes.collapseTab}
              onClick={toggleSidebar}
              size="small"
              aria-label={collapsed ? "Show sidebar" : "Hide sidebar"}
            >
              {collapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {/* Mobile: an in-drawer close handle (chevron-right) at the drawer's
            left edge so the open drawer can be dismissed without reaching the
            scrim. */}
        {isMobile && mobileOpen && (
          <Tooltip title="Hide" placement="left">
            <IconButton
              className={classes.collapseTab}
              onClick={() => setMobileOpen(false)}
              size="small"
              aria-label="Hide sidebar"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showBody && (
          <div className={classes.sidebarBody}>
            {isDragging && dropTarget === "above" && dropIndicator}
            {webcamPanelPosition === "above" ? (
              <>
                {WebcamPanel}
                {UserListSection}
              </>
            ) : (
              <>
                {UserListSection}
                {WebcamPanel}
              </>
            )}
            {isDragging && dropTarget === "below" && dropIndicator}
          </div>
        )}
      </Paper>
    </>
  )
}

export default RightSidebar
